import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../state/authStore";
import { AuthLayout, Field, FormError, PrimaryButton, inputClass } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";

const INVITE_PATTERN = /^INV-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}$/i;

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const register = useAuthStore((s) => s.register);
  const pending = useAuthStore((s) => s.pending);
  const errorMessage = useAuthStore((s) => s.errorMessage);

  const prefilled = new URLSearchParams(location.search).get("invite") || "";

  const [invite, setInvite] = useState(prefilled.toUpperCase());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);

    const trimmedInvite = invite.trim().toUpperCase();
    if (!INVITE_PATTERN.test(trimmedInvite)) {
      setLocalError("Code muss das Format INV-XXXX-XXXX-XXXX haben.");
      return;
    }
    if (password.length < 10) {
      setLocalError("Passwort muss mindestens 10 Zeichen lang sein.");
      return;
    }

    try {
      await register(email.trim().toLowerCase(), password, trimmedInvite);
      navigate("/", { replace: true });
    } catch {
      /* error already in store */
    }
  }

  const shownError = localError ?? errorMessage;

  return (
    <AuthLayout title="Konto anlegen" subtitle="Du brauchst einen Einladungscode.">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <Field
          label="Einladungscode"
          htmlFor="invite"
          hint="Format: INV-XXXX-XXXX-XXXX (4-stellige Hex-Blöcke)"
        >
          <input
            id="invite"
            name="invite"
            type="text"
            autoComplete="off"
            spellCheck={false}
            required
            value={invite}
            onChange={(e) => setInvite(e.target.value.toUpperCase())}
            disabled={pending}
            placeholder="INV-XXXX-XXXX-XXXX"
            className={`${inputClass} font-mono tracking-wide`}
            data-testid="invite-input"
          />
        </Field>
        <Field label="E-Mail" htmlFor="email">
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            className={inputClass}
            data-testid="email-input"
          />
        </Field>
        <Field label="Passwort" htmlFor="password" hint="Mindestens 10 Zeichen.">
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            minLength={10}
            disabled={pending}
          />
        </Field>
        {shownError && <FormError message={shownError} />}
        <PrimaryButton pending={pending} testid="submit-register">
          Konto anlegen
        </PrimaryButton>
        <div className="text-sm text-center pt-1">
          <Link to="/login" className="text-accent hover:underline">
            Schon ein Konto? Anmelden
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
